

















































































































import { Ability } from '@casl/ability'
import { Popover, Dialog, Button } from 'element-ui'
import { Component, Ref, Vue, Watch } from 'vue-property-decorator'

import AmountPaymentTab from '@/components/modals/AmountPaymentTab.vue'
import EnrollPaymentTab from '@/components/modals/EnrollPaymentTab.vue'
import EnrollTabBankPayment from '@/components/modals/EnrollTabBankPayment.vue'
import EnrollTabCardPayment from '@/components/modals/EnrollTabCardPayment.vue'
import EnrollTabTransferwisePayment from '@/components/modals/EnrollTabTransferwisePayment.vue'
import ModalSteps from '@/components/modals/ModalSteps.vue'
import SuccessPaymentTab from '@/components/modals/SuccessPaymentTab.vue'

import Booking from '@/models/booking'
import CreditCard from '@/models/credit-card'
import Customer from '@/models/customer'
import NewCreditCardForm from '@/models/new-credit-card-form'
import Passenger, { PassengerStatuses } from '@/models/passenger'
import Payment, { MINIMAL_PAYMENT_AMOUNT_CENTS } from '@/models/payment'
import PaymentAmountOptions from '@/models/payment-amount-options'
import PaymentModalSteps from '@/models/payment-modal-steps'
import PaymentPresetTypes from '@/models/payment-preset-types'

import getAbilitiesFor from '@/abilities/booking/abilities'
import EventBus from '@/event-bus'
import { toCurrency } from '@/globalMixin'
import { BookingsActionTypes } from '@/store/modules/bookings'
import { CreditCardActionTypes } from '@/store/modules/credit-cards'
import {
  PaymentModalMutationTypes,
  PaymentModalActionTypes,
} from '@/store/modules/paymentModal'

import PaymentModalTopMessage from './PaymentModalTopMessage.vue'

@Component({
  components: {
    [Dialog.name]: Dialog,
    [Button.name]: Button,
    [Popover.name]: Popover,
    AmountTab: AmountPaymentTab,
    EnrollTab: EnrollPaymentTab,
    ModalSteps,
    EnrollTabCardPayment,
    EnrollTabBankPayment,
    EnrollTabTransferwisePayment,
    SuccessTab: SuccessPaymentTab,
    PaymentModalTopMessage,
  },
})
export default class PaymentModal extends Vue {
  @Ref() readonly paymentModalBodyRef!: HTMLElement

  initialSetupInProgress = true
  paymentMethod = 'card'
  termsAndConditionsVisible = false
  amountToPay = 0
  depositsAmount = 0

  @Watch('bookingLoaded')
  onBookingLoadedChange(bookingLoaded: boolean) {
    if (bookingLoaded) {
      this.initialSetup()
    }
  }

  mounted() {
    EventBus.$on('on-bank-payment-open', () => {
      this.$store.dispatch(
        PaymentModalActionTypes.GetPaymentCode,
        this.enrollData,
      )
    })
    EventBus.$on('on-transferwise-payment-open', () => {
      this.$store.dispatch(
        PaymentModalActionTypes.GetPaymentCode,
        this.enrollData,
      )
    })

    this.initialSetup()
  }

  beforeDestroy() {
    EventBus.$off('on-bank-payment-open')
  }

  get booking(): Booking {
    return this.$store.getters.getBooking(this.$route.params.id)
  }

  get bookingLoaded(): boolean {
    return this.$store.state.bookings.bookingsLoaded
  }

  get passengers(): Passenger[] {
    if (!this.booking) {
      return []
    }

    return this.$store.getters.getPassengersByIds(this.booking.passengerIds)
  }

  get creditCards(): CreditCard[] {
    return this.$store.getters.getCreditCards
  }

  get enrollTabCardInfo() {
    return this.$store.getters.getEnrollTabCardInfo
  }

  get currentStep(): PaymentModalSteps {
    return this.$store.state.paymentModal.currentStep
  }

  get paymentAmountOption(): PaymentAmountOptions {
    return this.$store.state.paymentModal.paymentAmountOption
  }

  get paymentPresetType(): PaymentPresetTypes {
    return this.$store.state.paymentModal.paymentPresetType
  }

  get paymentBeingProcessed() {
    return this.$store.state.paymentModal.paymentBeingProcessed
  }

  get stripeSdkLoaded(): boolean {
    return this.$store.getters.getStripeSdkLoaded
  }

  get stripe(): boolean {
    return this.$store.getters.getStripe
  }

  get paymentCode(): string {
    return this.$store.state.paymentModal.paymentCode
  }

  get steps() {
    return PaymentModalSteps
  }

  get termsAccepted(): boolean {
    return this.$store.state.paymentModal.termsAccepted
  }

  get amountInvalid() {
    const { totalAmountInvalid, totalIsLessThanMin } = this

    return totalAmountInvalid || totalIsLessThanMin
  }

  get totalIsLessThanMin(): boolean {
    return !this.total || this.total < MINIMAL_PAYMENT_AMOUNT_CENTS
  }

  get totalAmountInvalid(): boolean {
    const { booking, total } = this

    const totalIsGreaterThanOutstanding =
      booking && total > booking.totalCents - booking.paidCents

    return totalIsGreaterThanOutstanding
  }

  // FIXME: remove this and only use amountToPay
  get total(): number {
    return this.amountToPay
  }

  get totalError(): string {
    switch (true) {
      case this.totalAmountInvalid:
        return 'Total amount is larger than remaining amount'
      case this.totalIsLessThanMin || isNaN(this.total):
        return `Minimal payment is ${toCurrency(
          MINIMAL_PAYMENT_AMOUNT_CENTS,
          this.booking.currencyCode,
        )}`
      default:
        return ''
    }
  }

  get enrollData() {
    const { booking, total, termsAccepted } = this

    return {
      currency: booking.currencyCode,
      recipients: [],
      enquiryId: booking.id,
      amountCents: total,
      termsAccepted,
    }
  }

  get currentCustomer(): Customer {
    return this.$store.getters.getCurrentCustomer
  }

  get title(): string {
    return this.currentStep === this.steps.Success
      ? 'Payment Successful'
      : 'Make Payment'
  }

  get newPayment(): Payment {
    const paymentId = this.$store.getters.getCompletedPaymentId
    return this.$store.getters.getPaymentById(paymentId)
  }

  get bookingAbility(): Ability {
    return getAbilitiesFor(this.booking)
  }

  initialSetup() {
    if (!this.bookingLoaded) {
      return
    }

    this.initialSetupInProgress = true
    this.checkIfAllowedToPay()
    this.setDefaultStep()
    this.autoselectPaymentPresetType()
    this.handleSpecialInitializationCases()
    this.initialSetupInProgress = false
  }

  checkIfAllowedToPay() {
    if (!this.bookingAbility.can('pay', 'Booking')) {
      this.$router.push({
        name: 'booking',
        params: { id: this.$route.params.id },
      })
    }
  }

  setDefaultStep() {
    this.setStep(PaymentModalSteps.Amount)
  }

  autoselectPaymentPresetType() {
    const activePassengers = this.passengers.filter(
      (p) => p.passengerStatus !== PassengerStatuses.Cancelled,
    )

    if (activePassengers.length === 1) {
      this.handleFullPaymentSpecialInitialization()
    }
  }

  handleSpecialInitializationCases() {
    const { initializeWith } = this.$route.params

    switch (initializeWith) {
      case 'fullPayment':
        this.handleFullPaymentSpecialInitialization()
        break
      case 'deposit':
        this.handleDepositSpecialInitialization()
        break
      default:
        break
    }
  }

  handleFullPaymentSpecialInitialization() {
    this.handlePaymentPresetTypeUpdate(PaymentPresetTypes.Full)
    this.handlePaymentAmountOptionUpdate(PaymentAmountOptions.Remaining)
    this.setStep(PaymentModalSteps.Amount)
  }

  handleDepositSpecialInitialization() {
    this.handlePaymentPresetTypeUpdate(PaymentPresetTypes.Deposit)
    this.setStep(PaymentModalSteps.Amount)
  }

  onUpdateEnrollTabInfo(changes: { name: string; value: any }) {
    this.$store.commit(PaymentModalMutationTypes.UpdateEnrollTabInfo, changes)
  }

  clearPaymentModal() {
    this.$store.commit(PaymentModalMutationTypes.ClearForm)
  }

  setStep(step: PaymentModalSteps): void {
    this.$nextTick(() => {
      const { amountInvalid } = this

      if (this.paymentBeingProcessed) {
        return
      }

      if (step === PaymentModalSteps.Amount || !amountInvalid) {
        this.$store.commit(PaymentModalMutationTypes.SetStep, step)
      }
    })
  }

  handleAmountToPayUpdate(amount: number) {
    this.amountToPay = amount * 100
  }

  handlePaymentAmountOptionUpdate(amountOption: PaymentAmountOptions) {
    this.$store.commit(
      PaymentModalMutationTypes.SetPaymentAmountOption,
      amountOption,
    )

    if (amountOption === PaymentAmountOptions.Remaining) {
      this.setRemainingAmountToPay()
    }
  }

  handlePaymentPresetTypeUpdate(presetType: PaymentPresetTypes) {
    this.$store.commit(
      PaymentModalMutationTypes.SetPaymentPresetType,
      presetType,
    )

    if (presetType === PaymentPresetTypes.Full) {
      this.handleAmountToPayUpdate(0)
      this.handlePaymentAmountOptionUpdate(PaymentAmountOptions.Custom)
    }

    if (presetType === PaymentPresetTypes.Deposit) {
      this.setDepositAmountToPay()
    }
  }

  handleDepositsAmountUpdate(depositsAmount: number): void {
    this.depositsAmount = depositsAmount || 0
    this.setDepositAmountToPay()
  }

  setRemainingAmountToPay() {
    this.amountToPay = this.booking.totalCents - this.booking.paidCents
  }

  setDepositAmountToPay() {
    const {
      depositsAmount,
      booking: {
        depositRemainingCents,
        depositPerPassengerCents,
        isCharter,
        isYachtCharter,
      },
    } = this

    if (isCharter || isYachtCharter) {
      this.amountToPay = depositRemainingCents
    } else {
      this.amountToPay = depositPerPassengerCents * depositsAmount
    }
  }

  onTermsAcceptedChange(termsAccepted: boolean) {
    this.$store.commit(
      PaymentModalMutationTypes.UpdateTermsAccepted,
      termsAccepted,
    )
    this.termsAndConditionsVisible = false
  }

  onBankTermsAcceptedChange(termsAccepted: boolean) {
    this.onTermsAcceptedChange(termsAccepted)

    if (termsAccepted) {
      this.$store.dispatch(
        PaymentModalActionTypes.GetPaymentCode,
        this.enrollData,
      )
    }
  }

  onPaymentModalClose() {
    if (this.currentStep === this.steps.Success) {
      this.clearPaymentModal()
    }
    this.$router.push({ name: 'booking', params: { id: this.booking.id } })
  }

  async handleEnroll(stripePaymentMethod: string) {
    const enrollForm = {
      ...this.enrollTabCardInfo,
      ...this.enrollData,
      stripePaymentMethod,
      stripe: this.stripe,
    }

    const stripePublicKey = this.booking.isYachtCharter
      ? (process.env.VUE_APP_STRIPE_EUR_ACC_PK as string)
      : (process.env.VUE_APP_STRIPE_PK as string)

    this.$store.commit(PaymentModalMutationTypes.EnrollStart)

    if (enrollForm.saveCard) {
      const cardInfo: NewCreditCardForm = {
        aliasName: enrollForm.cardAliasName,
        cardholderName: enrollForm.cardholderName,
        stripePaymentMethod,
        stripePublicKey,
      }
      await this.$store.dispatch(CreditCardActionTypes.AddCard, cardInfo)
    }

    await this.$store.dispatch(PaymentModalActionTypes.Enroll, enrollForm)
    this.$store.dispatch(BookingsActionTypes.Get, this.booking.id)
  }
}
